import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useSelector } from 'react-redux';
import api from '@/api';
import { getDayStartToEndRange } from '@/utils/dateutil';
import { showMedicalContentSelector } from '@/redux/config/selfData';
import { IGetNotificationRes } from '@/types/api/notification/getNotification';
import { IGetCareRecordRes } from '@/types/api/care-record/getCareRecord';
import { IGetResamples } from '@/types/api/ultra-sonic-data/getResample';
import { IGetSensorReaction, IGetSensorReactionRes } from '@/types/api/ultra-sonic-data/getSensorReaction';
import { IGetUrineTrendAnalysisRes } from '@/types/api/ultra-sonic-data/getUrineTrendAnalysis';
import { IGetActivityStatusAnalysisRes } from '@/types/api/ultra-sonic-data/getActivityStatusAnalysis';
import { IGetVisualizedUrineVolumeRes } from '@/types/api/medical/getVisualizedUrineVolume';
import { createUrineReport, updateWithThreshold, UrineReportData } from './createUrineReport';

// dayjs plugins
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface Props {
  start: Dayjs;
  end: Dayjs;
  residentId: string;
  timeRange: string;
  selectedStartTime: string;
  selectedEndTime: string;
  setThreshold: React.Dispatch<React.SetStateAction<string>>;
}

export type UrineGraphFetchFailedReason = 'TOO_LONG_PERIOD' | 'NO_DATA' | 'UNKNOWN_ERROR';

type UrineGraphFetchResult =
  | { success: true; data: UrineGraphSourceData }
  | { success: false; reason: UrineGraphFetchFailedReason };

export interface UrineGraphSourceData {
  urineTrend: IGetUrineTrendAnalysisRes;
  activityStatus: IGetActivityStatusAnalysisRes;
  sensorReactions: IGetSensorReactionRes;
  sensorResamples: IGetResamples;
  notifications: IGetNotificationRes[];
  careRecords: IGetCareRecordRes[];
}

export const useFetchUrineGraphData = () => {
  const showMedicalContent = useSelector(showMedicalContentSelector);

  // データ取得閾値 期間内にこれ以上のデータセットのcount総数がある場合はfetchを中止する
  const dataCountThreshold = 1000000;

  const [urineReportData, setUrineReportData] = React.useState<UrineReportData | undefined>(undefined);

  const fetchUrineGraphData = async ({
    start,
    end,
    residentId,
    timeRange,
    selectedStartTime,
    selectedEndTime,
    setThreshold,
  }: Props): Promise<UrineGraphFetchResult> => {
    const getTimeRange = (time: Dayjs) => {
      switch (timeRange) {
        case 'all-time': {
          // 今日の0時から翌日0時までの範囲を返す
          return getDayStartToEndRange(time, 0, 0);
        }
        case 'day-time': {
          // 今日の7時から19時までの範囲を返す
          const startHour = 7;
          const endHour = 19;
          return getDayStartToEndRange(time, startHour, endHour);
        }
        case 'night-time': {
          // 今日の19時から明日の7時までの範囲を返す
          const startHour = 19;
          const endHour = 7;
          return getDayStartToEndRange(time, startHour, endHour);
        }
        case 'selected-time': {
          // 自由設定時間を返す
          const startHour = +selectedStartTime.split(':')[0];
          const endHour = +selectedEndTime.split(':')[0];
          return getDayStartToEndRange(time, startHour, endHour);
        }
        default:
          throw new Error('Invalid selected timeRange');
      }
    };

    const getDatas = async (from: string, to: string): Promise<UrineGraphFetchResult> => {
      const convertVisualizedUrineVolumeToSensorReaction = (
        data: IGetVisualizedUrineVolumeRes,
      ): IGetSensorReactionRes => {
        return (data || []).map((item) => {
          const normalizedValue = Math.min(10, Math.max(0, Math.round((item.value || 0) / 10)));
          return {
            timestamp: item.timestamp,
            activeRatio: normalizedValue / 10,
            u1xActiveRatio: normalizedValue / 10,
          } as IGetSensorReaction;
        });
      };

      const preCheckDataCount = async (): Promise<boolean> => {
        const dataset = await api.get('/residents/:residentId/ultra-sonic-data-sets', {
          params: { residentId, from, to, limit: 1000 },
        });
        const total = dataset.reduce((sum: number, item: any) => sum + (item.count ?? item.number ?? 0), 0);

        return total > dataCountThreshold;
      };

      const getUrineGraphSourceDatas = async (): Promise<UrineGraphFetchResult> => {
        // 排尿・活動傾向のデータ取得は先頭のレコードが落ちないように開始時間を1分前にする
        const trendStartTime = dayjs(from).subtract(1, 'minute').utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]');
        const params = { residentId, from, to, limit: dataCountThreshold } as const;
        const urineTrendParams = {
          ...params,
          from: trendStartTime,
        } as const;

        const [urineTrend, activityStatus, sensorReactions, sensorResamples, notifications, careRecords] =
          await Promise.all([
            showMedicalContent
              ? api.get('/md/residents/:residentId/urine-trend-analysis', { params: urineTrendParams })
              : api.get('/residents/:residentId/urine-trend-analysis', { params: urineTrendParams }),
            showMedicalContent
              ? api.get('/md/residents/:residentId/activity-status-analysis', { params: urineTrendParams })
              : api.get('/residents/:residentId/activity-status-analysis', { params: urineTrendParams }),
            showMedicalContent
              ? api.get('/md/residents/:residentId/visualized-urine-volume', { params })
              : api.get('/residents/:residentId/sensor-reactions', { params }),
            showMedicalContent
              ? Promise.resolve([])
              : api.get('/traversal/residents/:residentId/sensor-reaction-resamples', { params }),
            api.get('/residents/:residentId/notifications', { params }),
            api.get('/residents/:residentId/care-records', { params }),
          ]);
        const normalizedSensorReactions = showMedicalContent
          ? convertVisualizedUrineVolumeToSensorReaction(sensorReactions as unknown as IGetVisualizedUrineVolumeRes)
          : (sensorReactions as IGetSensorReactionRes);

        if (!urineTrend.length && !normalizedSensorReactions.length && !careRecords.length) {
          return { success: false, reason: 'NO_DATA' };
        }

        const result: UrineGraphSourceData = {
          urineTrend,
          activityStatus,
          sensorReactions: normalizedSensorReactions,
          sensorResamples,
          notifications,
          careRecords,
        };

        return { success: true, data: result };
      };

      try {
        const isOverThreshold: boolean = await preCheckDataCount();

        if (isOverThreshold) {
          return { success: false, reason: 'TOO_LONG_PERIOD' };
        }

        return getUrineGraphSourceDatas();
      } catch (e) {
        return { success: false, reason: 'UNKNOWN_ERROR' };
      }
    };

    const [startTime] = getTimeRange(dayjs(start));
    const [, endTime] = getTimeRange(dayjs(end));

    const from = startTime.format('YYYY-MM-DD[T]HH:mm:ss[Z]');
    const to = endTime.subtract(1, 'minute').format('YYYY-MM-DD[T]HH:mm:ss[Z]');

    const result = await getDatas(from, to);

    if (result.success) {
      const updatedThreshold = updateThreshold(result.data.urineTrend);
      setThreshold(updatedThreshold);

      const urineReportData = createUrineReport(
        result.data,
        +updatedThreshold,
        timeRange,
        selectedStartTime,
        selectedEndTime,
      );

      setUrineReportData(urineReportData);
    }

    return result;
  };

  const updateThresholdRelatedData = (threshold: string) => {
    if (urineReportData) {
      const updated = updateWithThreshold(+threshold, urineReportData);

      if (updated) {
        setUrineReportData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            ...{
              overThresholdHeatmap: updated.updatedOverThresholdHeatmap,
              detectUrinationFlags: updated.updatedDetectUrinationFlags,
            },
          };
        });
      }
    }
  };

  return { urineReportData, fetchUrineGraphData, updateThresholdRelatedData };
};

const updateThreshold = (data: IGetUrineTrendAnalysisRes): string => {
  const levelCounts: Record<number, number> = {};

  data
    .filter((value) => value.urineLevel > 0)
    .forEach((value) => {
      const level = value.urineLevel;
      levelCounts[level] = (levelCounts[level] || 0) + 1;
    });

  let maxCount = 0;
  let mode = 0;
  for (const level in levelCounts) {
    const count = levelCounts[Number(level)];
    if (count > maxCount || (count === maxCount && Number(level) > mode)) {
      maxCount = count;
      mode = Number(level);
    }
  }

  return JSON.stringify(mode);
};
